import { getCapabilities, getMakesForYear, getModelsForMake } from './ymm';
import type { Capabilities } from './types';

export const YMM_QUERY_PARAMS = ['year', 'make', 'model'] as const;

export interface QueryStringVehicle {
    year: string;
    make: string;
    model: string;
    id: string;
    capabilities: Capabilities;
}

function sameText(a: string, b: string): boolean {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/**
 * Reads `year`, `make` and `model` (and nothing else) from a query string and validates them against
 * the YMM service. Returns the vehicle using the service's own casing, or `undefined` when a param is
 * missing or the combination doesn't match an available vehicle, so callers can fall back to the
 * existing behavior.
 */
export async function resolveVehicleFromQueryString(search: string): Promise<QueryStringVehicle | undefined> {
    const params = new URLSearchParams(search);
    const year = params.get('year')?.trim();
    const make = params.get('make')?.trim();
    const model = params.get('model')?.trim();

    if (!year || !make || !model) {
        return undefined;
    }

    const matchedMake = (await getMakesForYear(year)).find((candidate) => sameText(candidate, make));
    if (!matchedMake) {
        return undefined;
    }

    const matchedModel = (await getModelsForMake(year, matchedMake)).find((candidate) =>
        sameText(candidate.vehicleModel, model),
    );
    if (!matchedModel) {
        return undefined;
    }

    const capabilities = await getCapabilities(matchedModel.ymmComboId);
    if (!capabilities) {
        return undefined;
    }

    return {
        year,
        make: matchedMake,
        model: matchedModel.vehicleModel,
        id: matchedModel.ymmComboId,
        capabilities,
    };
}
